import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "./utils";

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        
        primary: "border-transparent bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-dark)]",
        success: "border-transparent bg-[var(--color-success)] text-white hover:bg-[var(--color-success-dark)]",
        info: "border-transparent bg-[var(--color-info)] text-white hover:bg-[var(--color-info-dark)]",
        warning: "border-transparent bg-[var(--color-warning)] text-white hover:bg-[var(--color-warning-dark)]",
        danger: "border-transparent bg-[var(--color-danger)] text-white hover:bg-[var(--color-danger-dark)]",
        light: "border-transparent bg-[var(--color-light)] text-[var(--color-dark)] hover:bg-[var(--color-light-dark)]",
        dark: "border-transparent bg-[var(--color-dark)] text-white hover:bg-[var(--color-dark-dark)]",

        "outline-primary": "bg-transparent border border-[var(--color-primary)] text-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:text-white",
        "outline-success": "bg-transparent border border-[var(--color-success)] text-[var(--color-success)] hover:bg-[var(--color-success)] hover:text-white",
        "outline-info": "bg-transparent border border-[var(--color-info)] text-[var(--color-info)] hover:bg-[var(--color-info)] hover:text-white",
        "outline-warning": "bg-transparent border border-[var(--color-warning)] text-[var(--color-warning)] hover:bg-[var(--color-warning)] hover:text-white",
        "outline-danger": "bg-transparent border border-[var(--color-danger)] text-[var(--color-danger)] hover:bg-[var(--color-danger)] hover:text-white",
        "outline-light": "bg-transparent border border-[var(--color-light)] text-[var(--color-light)] hover:bg-[var(--color-light)] hover:text-dark",
        "outline-dark": "bg-transparent border border-[var(--color-dark)] text-[var(--color-dark)] hover:bg-[var(--color-dark)] hover:text-white",

        "soft-primary": "border-transparent bg-[var(--color-primary)] bg-opacity-10 text-[var(--color-primary)] hover:bg-opacity-20",
        "soft-success": "border-transparent bg-[var(--color-success)] bg-opacity-10 text-[var(--color-success)] hover:bg-opacity-20",
        "soft-info": "border-transparent bg-[var(--color-info)] bg-opacity-10 text-[var(--color-info)] hover:bg-opacity-20",
        "soft-warning": "border-transparent bg-[var(--color-warning)] bg-opacity-10 text-[var(--color-warning)] hover:bg-opacity-20",
        "soft-danger": "border-transparent bg-[var(--color-danger)] bg-opacity-10 text-[var(--color-danger)] hover:bg-opacity-20",
        "soft-light": "border-transparent bg-[var(--color-light)] bg-opacity-50 text-[var(--color-dark)] hover:bg-opacity-70",
        "soft-dark": "border-transparent bg-[var(--color-dark)] bg-opacity-10 text-[var(--color-dark)] hover:bg-opacity-20",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  asChild?: boolean;
}

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: BadgeProps) {
  const Comp = asChild ? Slot : "span";

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
